import React, { memo, useCallback, useRef, useState } from "react";
import { Button, Input } from "antd";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import { useLocation } from "react-router-dom";

import styles from "./styles.module.scss";
import { GetImg } from "utils";
import { IMessageThread, TStore } from "models";
import { sendMessageAction } from "store";

const stateSelectorHandle = createSelector(
	(state: TStore) => state.auth.credentials?.user,
	(user) => ({ user }),
);

interface IMessengerEditor {
	selectedThread: IMessageThread | null;
}

const MessengerEditor: React.FunctionComponent<IMessengerEditor> = ({
	selectedThread,
}) => {
	const dispatch = useDispatch();
	const [textareaValue, setTextareaValue] = useState<string>("");
	const stateSelector = useCallback(stateSelectorHandle, []);
	const { user } = useSelector(stateSelector);
	const shiftPressed = useRef(false);

	const handleKeyUp = useCallback((e) => {
		if (e.keyCode === 16) shiftPressed.current = false;
	}, []);

	const location = useLocation();

	const handleTextareaChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (event.target.value === "\n") {
				setTextareaValue("");
				return;
			}

			const value = event.target.value;
			setTextareaValue(value);
		},
		[],
	);

	const sendMessage = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			// TODO: this function need to updated because is not called on enter key anymore
			if (
				!shiftPressed.current &&
				textareaValue.length &&
				selectedThread &&
				(selectedThread.user?.id ||
					selectedThread.user?.id ||
					(selectedThread.users &&
						selectedThread.users[0] &&
						selectedThread.users[0].id))
			) {
				let recipientId = 0;
				if (selectedThread.id === 0) {
					if (location.state?.post) {
						recipientId = selectedThread.user?.id
							? selectedThread.user?.id
							: selectedThread.users
							? selectedThread.users[0].id
							: 0;
					} else {
						recipientId = selectedThread.user?.membership_id
							? selectedThread.user?.membership_id
							: selectedThread.users
							? selectedThread.users[0].id
							: 0;
					}
				} else {
					recipientId = selectedThread.user?.id
						? selectedThread.user?.id
						: selectedThread.users
						? selectedThread.users[0].id
						: 0;
				}

				const postId =
					selectedThread.community_post_id ||
					(selectedThread.activity_type === "community_post" &&
						selectedThread.id) ||
					0;

				dispatch(
					sendMessageAction({
						postId,
						content: textareaValue.trim(),
						recipientId: recipientId!,
						conversationId:
							selectedThread.conversation_id || selectedThread.id,
					}),
				);
				setTextareaValue("");
			}
		},
		[dispatch, location.state?.post, selectedThread, textareaValue],
	);

	const handleKeyDown = useCallback(
		(e) => {
			if (e.keyCode === 16) shiftPressed.current = true;
			if (e.key === "Enter") sendMessage(e);
			if (e.key === "Enter" && e.shiftKey === true) {
				e.preventDefault();
				setTextareaValue(textareaValue + "\n");
			}
		},
		[sendMessage, textareaValue],
	);

	return (
		<div className={styles.container}>
			<GetImg endpoint={user?.picture} className={styles.avatar} />
			<div className={classNames(styles.textContainer)}>
				<Input.TextArea
					placeholder={"Message"}
					onKeyUp={handleKeyUp}
					onKeyDown={handleKeyDown}
					value={textareaValue}
					onChange={handleTextareaChange}
				/>
				<div className={classNames(styles.sendButtonContainer)}>
					<Button disabled={!textareaValue} onClick={sendMessage}>
						{"Send"}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default memo(MessengerEditor);
